import { useMemo, useState } from 'react';
import { brandAs, type Seed, type TaxonomyId } from '../../core/model/brands.ts';
import type { CanonicalExam, Difficulty } from '../../core/model/canonical.ts';
import {
  buildBlueprint,
  countMatching,
  randomSeed,
  type SessionBlueprint,
} from '../../core/exam-engine/blueprint.ts';
import {
  ALL_QUESTIONS,
  presetFor,
  sanitizePolicy,
  sanitizeRange,
  type ModePreset,
  type SelectionCriteria,
  type SessionPolicy,
} from '../../core/exam-engine/policy.ts';
import type { ExamHistory } from '../../core/model/history.ts';
import { QUESTION_TYPES, type QuestionType } from '../../core/schema/question-types.ts';
import { useI18n } from '../../i18n/index.tsx';
import { Button, Callout } from '../../ui/primitives.tsx';

export interface StartRequest {
  readonly blueprint: SessionBlueprint;
  readonly policy: SessionPolicy;
  readonly criteria: SelectionCriteria;
  readonly modePreset: ModePreset;
  readonly candidateName: string;
}

const MODES: readonly ModePreset[] = ['exam', 'practice', 'custom'];
const DIFFICULTIES: readonly Difficulty[] = ['easy', 'medium', 'hard'];

/**
 * Choosing what this session will be.
 *
 * The screen is driven by the exam it was given: only the categories, topics and tags that actually
 * appear are offered, and the live count says how many questions the current filters would draw.
 * The same rule governs the history filter — it appears only once this device has attempts to
 * answer it with, because a control that can only ever return nothing teaches people that the
 * filters do not work.
 */
export function ConfigScreen({
  exam,
  history,
  remembering,
  onRemember,
  onForget,
  onStart,
  onBack,
}: {
  readonly exam: CanonicalExam;
  readonly history: ExamHistory;
  readonly remembering: boolean;
  readonly onRemember: (value: boolean) => void;
  readonly onForget: () => void;
  readonly onStart: (request: StartRequest) => void;
  readonly onBack: () => void;
}) {
  const { t } = useI18n();

  const [mode, setMode] = useState<ModePreset>('exam');
  const [policy, setPolicy] = useState<SessionPolicy>(() => presetFor('exam', exam));
  const [criteria, setCriteria] = useState<SelectionCriteria>(ALL_QUESTIONS);
  const [seed, setSeed] = useState<Seed>(() => randomSeed());
  const [candidateName, setCandidateName] = useState('');

  const total = exam.questions.length;
  const matching = useMemo(
    () => countMatching(exam, criteria, history),
    [exam, criteria, history],
  );
  /** How many questions this device remembers getting wrong at least once. */
  const troublesome = useMemo(
    () => Object.values(history).filter((attempts) => attempts.incorrect > 0).length,
    [history],
  );

  const tags = useMemo(() => Object.keys(exam.index.byTag).sort(), [exam]);
  const typesPresent = useMemo(
    () => QUESTION_TYPES.filter((type) => exam.index.byType[type].length > 0),
    [exam],
  );

  const chooseMode = (next: ModePreset): void => {
    setMode(next);
    setPolicy(presetFor(next, exam));
  };

  const patchCriteria = (patch: Partial<SelectionCriteria>): void => {
    setCriteria((current) => ({ ...current, ...patch }));
  };

  const toggle = <T,>(list: readonly T[], value: T): T[] =>
    list.includes(value) ? list.filter((entry) => entry !== value) : [...list, value];

  const start = (): void => {
    const effective = sanitizePolicy(policy);
    // Repaired here rather than on every keystroke: a range is half-typed for as long as it takes
    // to type it, and a field that rewrites itself under the cursor is unusable.
    const effectiveCriteria: SelectionCriteria = {
      ...criteria,
      range: sanitizeRange(criteria.range, total),
    };
    const { blueprint } = buildBlueprint(exam, effectiveCriteria, effective, seed, history);
    onStart({
      blueprint,
      policy: effective,
      criteria: effectiveCriteria,
      modePreset: mode,
      candidateName,
    });
  };

  return (
    <section className="screen screen--config" aria-labelledby="config-title">
      <h1 id="config-title">{t('config.title')}</h1>

      <label className="field">
        <span>{t('config.candidateName')}</span>
        <input
          type="text"
          value={candidateName}
          maxLength={120}
          autoComplete="off"
          placeholder={t('config.candidateNamePlaceholder')}
          onChange={(event) => {
            setCandidateName(event.target.value);
          }}
        />
        <span className="field__help">{t('config.candidateNameHelp')}</span>
      </label>

      <fieldset className="field-group">
        <legend>{t('config.mode')}</legend>
        <div className="modes">
          {MODES.map((candidate) => (
            <label key={candidate} className={`mode${mode === candidate ? ' mode--selected' : ''}`}>
              <input
                type="radio"
                name="mode"
                value={candidate}
                checked={mode === candidate}
                onChange={() => {
                  chooseMode(candidate);
                }}
              />
              <span className="mode__name">{t(`config.mode.${candidate}` as const)}</span>
              <span className="mode__description">
                {t(`config.mode.${candidate}.description` as const)}
              </span>
            </label>
          ))}
        </div>
      </fieldset>

      {/* The timer is not part of a mode. Exam mode suggests one and practice suggests none, but
          "timed practice" is a thing people want, so the control is always here. */}
      <fieldset className="field-group">
        <legend>{t('config.timer')}</legend>

        <label className="checkbox">
          <input
            type="checkbox"
            checked={policy.timeLimitMs !== null}
            onChange={(event) => {
              setPolicy({
                ...policy,
                timeLimitMs: event.target.checked ? defaultTimeLimitMs(exam) : null,
              });
            }}
          />
          <span>{t('config.timerOn')}</span>
        </label>

        {policy.timeLimitMs !== null && (
          <label className="field">
            <span>{t('config.timeLimit')}</span>
            <input
              type="number"
              min={1}
              max={1440}
              value={Math.round(policy.timeLimitMs / 60_000)}
              onChange={(event) => {
                const minutes = Number(event.target.value);
                setPolicy({
                  ...policy,
                  timeLimitMs: Number.isFinite(minutes) && minutes > 0 ? minutes * 60_000 : null,
                });
              }}
            />
          </label>
        )}
      </fieldset>

      <fieldset className="field-group">
        <legend>{t('config.filters')}</legend>

        {exam.taxonomy.categories.length > 0 && (
          <CheckboxGroup
            label={t('config.category')}
            options={exam.taxonomy.categories.map((node) => ({ value: node.id, label: node.label }))}
            selected={criteria.categoryIds}
            onToggle={(value) => {
              patchCriteria({ categoryIds: toggle(criteria.categoryIds, value as TaxonomyId) });
            }}
          />
        )}

        {exam.taxonomy.topics.length > 0 && (
          <CheckboxGroup
            label={t('config.topic')}
            options={exam.taxonomy.topics.map((node) => ({ value: node.id, label: node.label }))}
            selected={criteria.topicIds}
            onToggle={(value) => {
              patchCriteria({ topicIds: toggle(criteria.topicIds, value as TaxonomyId) });
            }}
          />
        )}

        <CheckboxGroup
          label={t('config.difficulty')}
          options={DIFFICULTIES.filter(
            (level) => (exam.index.byDifficulty[level]?.length ?? 0) > 0,
          ).map((level) => ({ value: level, label: t(`difficulty.${level}` as const) }))}
          selected={criteria.difficulties}
          onToggle={(value) => {
            patchCriteria({ difficulties: toggle(criteria.difficulties, value as Difficulty) });
          }}
        />

        <CheckboxGroup
          label={t('config.type')}
          options={typesPresent.map((type) => ({ value: type, label: t(`type.${type}` as const) }))}
          selected={criteria.types}
          onToggle={(value) => {
            patchCriteria({ types: toggle(criteria.types, value as QuestionType) });
          }}
        />

        {tags.length > 0 && (
          <CheckboxGroup
            label={t('config.tag')}
            options={tags.map((tag) => ({ value: tag, label: tag }))}
            selected={criteria.tags}
            onToggle={(value) => {
              patchCriteria({ tags: toggle(criteria.tags, value) });
            }}
          />
        )}

        {/* Only offered once this device actually has something to answer it with — a filter that
            can only ever return nothing teaches people that the filters do not work. */}
        {troublesome > 0 && (
          <fieldset className="checkbox-group">
            <legend>{t('config.weakest')}</legend>
            <label className="checkbox">
              <input
                type="checkbox"
                checked={criteria.incorrectAtLeast !== null}
                onChange={(event) => {
                  patchCriteria({ incorrectAtLeast: event.target.checked ? 1 : null });
                }}
              />
              <span>{t('config.onlyIncorrect', { count: troublesome })}</span>
            </label>

            {criteria.incorrectAtLeast !== null && (
              <label className="field field--inline">
                <span>{t('config.incorrectAtLeast')}</span>
                <input
                  type="number"
                  min={1}
                  max={99}
                  value={criteria.incorrectAtLeast}
                  onChange={(event) => {
                    const times = Number(event.target.value);
                    patchCriteria({
                      incorrectAtLeast: Number.isFinite(times) && times > 0 ? Math.floor(times) : 1,
                    });
                  }}
                />
              </label>
            )}
          </fieldset>
        )}
      </fieldset>

      <fieldset className="field-group">
        <legend>{t('config.questionCount')}</legend>

        <p className={matching === 0 ? 'count count--empty' : 'count'} role="status">
          {matching === 0
            ? t('config.noMatches')
            : matching === 1
              ? t('config.matchingOne')
              : t('config.matching', { count: matching })}
        </p>

        <label className="field">
          <span>{t('config.questionCount')}</span>
          <input
            type="number"
            min={1}
            max={Math.max(1, matching)}
            value={criteria.count === 'all' ? '' : criteria.count}
            placeholder={t('config.all', { count: matching })}
            onChange={(event) => {
              const value = Number(event.target.value);
              patchCriteria({ count: Number.isFinite(value) && value > 0 ? value : 'all' });
            }}
          />
        </label>

        {/* Numbered by the file, not by the session: "questions 1 to 50" has to mean the same
            thing whether or not this session shuffles, and those are the numbers shown on screen
            beside each question. */}
        <label className="checkbox">
          <input
            type="checkbox"
            checked={criteria.range !== null}
            onChange={(event) => {
              patchCriteria({
                range: event.target.checked ? { from: 1, to: Math.min(total, 50) } : null,
              });
            }}
          />
          <span>{t('config.useRange')}</span>
        </label>

        {criteria.range !== null && (
          <div className="range">
            <label className="field field--inline">
              <span>{t('config.rangeFrom')}</span>
              <input
                type="number"
                min={1}
                max={total}
                value={criteria.range.from}
                onChange={(event) => {
                  patchCriteria({
                    range: { from: Number(event.target.value), to: criteria.range?.to ?? total },
                  });
                }}
              />
            </label>
            <label className="field field--inline">
              <span>{t('config.rangeTo')}</span>
              <input
                type="number"
                min={1}
                max={total}
                value={criteria.range.to}
                onChange={(event) => {
                  patchCriteria({
                    range: { from: criteria.range?.from ?? 1, to: Number(event.target.value) },
                  });
                }}
              />
            </label>
            <p className="field__help">{t('config.rangeHelp', { total })}</p>
          </div>
        )}
      </fieldset>

      <fieldset className="field-group">
        <legend>{t('config.seed')}</legend>
        <p className="field__help">{t('config.seedHelp')}</p>

        <div className="seed">
          <label className="field">
            <span className="visually-hidden">{t('config.seed')}</span>
            <input
              type="text"
              value={seed}
              onChange={(event) => {
                setSeed(brandAs<Seed>(event.target.value));
              }}
            />
          </label>
          <Button
            onClick={() => {
              setSeed(randomSeed());
            }}
          >
            {t('config.newSeed')}
          </Button>
        </div>
      </fieldset>

      {mode === 'custom' && <CustomPolicy policy={policy} onChange={setPolicy} />}

      {/* The only place this application writes to the device, and the only place that says so. */}
      <fieldset className="field-group">
        <legend>{t('config.device')}</legend>

        <label className="checkbox">
          <input
            type="checkbox"
            checked={remembering}
            onChange={(event) => {
              onRemember(event.target.checked);
            }}
          />
          <span>{t('config.remember')}</span>
        </label>
        <p className="field__help">{t('config.rememberHelp')}</p>

        {troublesome > 0 && (
          <p className="field__help">{t('config.remembered', { count: troublesome })}</p>
        )}

        <Button
          variant="quiet"
          onClick={() => {
            onForget();
          }}
        >
          {t('config.forget')}
        </Button>
      </fieldset>

      {matching === 0 && (
        <Callout tone="warning">
          <p>{t('config.noMatches')}</p>
        </Callout>
      )}

      <div className="actions">
        <Button variant="primary" onClick={start} disabled={matching === 0}>
          {t('config.start')}
        </Button>
        <Button onClick={onBack}>{t('config.back')}</Button>
      </div>
    </section>
  );
}

function CheckboxGroup({
  label,
  options,
  selected,
  onToggle,
}: {
  readonly label: string;
  readonly options: readonly { readonly value: string; readonly label: string }[];
  readonly selected: readonly string[];
  readonly onToggle: (value: string) => void;
}) {
  if (options.length === 0) {
    return null;
  }

  return (
    <fieldset className="checkbox-group">
      <legend>{label}</legend>
      <div className="checkbox-group__options">
        {options.map((option) => (
          <label key={option.value} className="checkbox">
            <input
              type="checkbox"
              checked={selected.includes(option.value)}
              onChange={() => {
                onToggle(option.value);
              }}
            />
            <span>{option.label}</span>
          </label>
        ))}
      </div>
    </fieldset>
  );
}

/** The raw policy, for the mode that is defined as "the raw policy". */
function CustomPolicy({
  policy,
  onChange,
}: {
  readonly policy: SessionPolicy;
  readonly onChange: (policy: SessionPolicy) => void;
}) {
  const { t } = useI18n();

  const switches: readonly { key: keyof SessionPolicy; label: string }[] = [
    { key: 'shuffleQuestions', label: t('config.shuffleQuestions') },
    { key: 'shuffleOptions', label: t('config.shuffleOptions') },
    { key: 'immediateFeedback', label: t('config.immediateFeedback') },
    { key: 'allowBackNavigation', label: t('config.allowBack') },
    { key: 'allowChangeAnswer', label: t('config.allowChange') },
  ];

  return (
    <fieldset className="field-group">
      <legend>{t('config.mode.custom')}</legend>

      {switches.map((entry) => (
        <label key={entry.key} className="checkbox">
          <input
            type="checkbox"
            checked={policy[entry.key] === true}
            onChange={(event) => {
              const next = { ...policy, [entry.key]: event.target.checked };
              // Turning immediate feedback on is what practice mode means; the two dependent
              // switches follow it rather than being separately forgettable.
              onChange(
                entry.key === 'immediateFeedback'
                  ? {
                      ...next,
                      showExplanationAfterAnswer: event.target.checked,
                      showOptionFeedback: event.target.checked,
                    }
                  : next,
              );
            }}
          />
          <span>{entry.label}</span>
        </label>
      ))}
    </fieldset>
  );
}

/** What "on" means when someone switches the timer on: the exam's own suggestion, or an hour. */
function defaultTimeLimitMs(exam: CanonicalExam): number {
  return (exam.meta.defaults.timeLimitMinutes ?? 60) * 60_000;
}
